import { forwardRef, KeyboardEvent, useCallback, useMemo, useRef } from 'react';
import Select, {
  ActionMeta,
  components,
  createFilter,
  GroupBase,
  OnChangeValue,
  OptionProps,
  OptionsOrGroups,
  Props,
  StylesConfig,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';

import {
  DropdownPlaceholder,
  DropdownMultiValueContainer,
  DropdownIndicator,
  DropdownMenuList,
  DropdownValueContainer,
  DropdownOption,
} from './components';
import themeColors from './dropdown.module.scss';

export interface SelectOption {
  label: string;
  value?: string | number | readonly string[] | null;
  isNew?: boolean;
}

export interface GroupedOption {
  label: string;
  options: SelectOption[];
}

export interface OptionPropsExtra extends OptionProps {
  onDeleteOption?: (value: string) => void;
}

export type Options = OptionsOrGroups<SelectOption | GroupedOption, GroupBase<SelectOption | GroupedOption>>;
// Overwrite the module declaration to be able to add custom props. See here: https://react-select.com/typescript
declare module 'react-select/dist/declarations/src/Select' {
  export interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
    allowCreation?: boolean;
    addEmptyOption?: boolean;
    dataTestId?: string;
    emptyOptionLabel?: string;
    initiallyFocused?: boolean;
    isAsync?: boolean;
    label?: string;
    required?: boolean;
    hasErrors?: boolean;
    showDropdownIndicator?: boolean;
    prefixIcon?: string;
    loadOptions?: (inputValue: string, callback: (options: Option[]) => void) => void;
    onCreateOption?: (title: string) => Promise<Option>;
    handleKeyDown?: (e: KeyboardEvent<HTMLDivElement>, isMenuOpen: boolean) => void;
    onClear?: () => void;
    handleChange?: (option: OnChangeValue<Option, IsMulti>) => void;
    defaultOptions?: Option[];
    onDeleteOption?: (value: string) => void;
  }
}

type Group = GroupBase<SelectOption>;

export type DropdownProps<IsMulti extends boolean = false> = Props<SelectOption, IsMulti, Group> & {
  prefixIcon?: string;
  htmlFor?: string;
};

export const Dropdown = forwardRef(<IsMulti extends boolean = false>(props: DropdownProps<IsMulti>, ref) => {
  const {
    prefixIcon,
    allowCreation = true,
    className,
    isAsync = false,
    isMulti: isMultiple = true,
    initiallyFocused = false,
    placeholder = '',
    required = false,
    hasErrors = false,
    addEmptyOption = !required,
    showDropdownIndicator = !isMultiple,
    isDisabled = false,
    isSearchable = allowCreation,
    instanceId,
    dataTestId,
    label,
    options,
    value,
    loadOptions,
    onClear,
    onCreateOption,
    handleKeyDown: onKeyDown,
    filterOption = createFilter(filterConfig),
    onDeleteOption,
    htmlFor,
    ...restProps
  } = props;

  const menuOpenRef = useRef(true);

  if (isAsync && !loadOptions) {
    throw new Error('Dropdown: Async select needs a load options function!');
  }

  if (!isAsync && !options) {
    throw new Error('Dropdown: Select needs options');
  }

  const getOptions = () => {
    if (isAsync) return undefined;
    if (addEmptyOption) {
      return options
        ? [{ label: props.emptyOptionLabel || '', value: undefined }, ...options]
        : [{ label: props.emptyOptionLabel || '', value: undefined }];
    }
    return options;
  };

  const _options = getOptions();

  const _onChange = async (newValue: OnChangeValue<SelectOption, false | true>, actionMeta: ActionMeta<SelectOption>) => {
    if (actionMeta.action === 'clear') {
      onClear?.();
    } else if (actionMeta.action === 'create-option') {
      const updatedOption = await onCreateOption?.(actionMeta.option.label);
      const updatedValues = Array.isArray(newValue) ? newValue.map((value) => (value.__isNew__ ? updatedOption : value)) : updatedOption;
      const _props = props as Props<SelectOption, true, Group>;
      _props.handleChange?.(updatedValues as SelectOption[]);
    } else {
      const _props = props as Props<SelectOption, false, Group>;
      newValue !== null && _props.handleChange?.(newValue as SelectOption);
    }
  };

  const closeMenu = () => {
    menuOpenRef.current = true;
  };

  const openMenu = () => {
    menuOpenRef.current = false;
  };

  const _onKeyDown = (event?: KeyboardEvent<HTMLDivElement>) => {
    if (event) {
      const nodeId = (event.target as HTMLDivElement).id as string;
      nodeId.includes('react-select') && onKeyDown?.(event, menuOpenRef.current);
    }
  };
  const showIndicators = useMemo(() => showDropdownIndicator || !!onClear, [showDropdownIndicator, onClear]);

  const _IndicatorsContainer = useMemo(() => IndicatorsContainer(showIndicators), [showIndicators]);
  const _DropdownIndicator = useMemo(() => DropdownIndicator(showDropdownIndicator), [showDropdownIndicator]);
  const _ClearIndicator = useMemo(() => ClearIndicator(!!onClear), [onClear]);
  const _MenuList = useMemo(
    () => DropdownMenuList(!!restProps.noOptionsMessage, !!restProps.loadingMessage),
    [restProps.noOptionsMessage, restProps.loadingMessage]
  );
  const _DropdownOption = useCallback(
    (optionProps: OptionPropsExtra) => DropdownOption({ ...optionProps, onDeleteOption }),
    [onDeleteOption]
  );

  const selectProps = {
    ...restProps,
    prefixIcon,
    placeholder,
    onChange: _onChange,
    theme: (theme) => ({
      ...theme,
      borderRadius: 0,
    }),
    className: 'stihl-text-body-default-reg bg-primary-white',
    components: {
      MultiValueContainer: DropdownMultiValueContainer,
      IndicatorSeparator,
      Placeholder: DropdownPlaceholder,
      ValueContainer: DropdownValueContainer,
      MenuList: _MenuList,
      IndicatorsContainer: _IndicatorsContainer,
      DropdownIndicator: _DropdownIndicator,
      ClearIndicator: _ClearIndicator,
      Option: _DropdownOption,
      ...props.components,
    },
    options: _options,
    isMulti: isMultiple,
    styles: {
      valueContainer: (provided) => ({
        ...provided,
        padding: isMultiple ? '2px 8px' : '0 8px',
      }),
      control: (_, state) => ({
        display: 'flex',
        minHeight: '3rem',
        border: `1px solid ${hasErrors ? themeColors.redDefault : state.isFocused ? themeColors.primaryBlack : themeColors.secondary}`,
        outlineOffset: 0,
        ':hover': {
          border: `1px solid ${themeColors.primaryBlack}`,
        },
        outline: state.isFocused ? `1px solid ${themeColors.primaryBlack}` : '2px solid transparent',
      }),
      ...customStyles,
      ...props.styles,
    },
    defaultValue: value,
    onMenuOpen: openMenu,
    onMenuClose: closeMenu,
    closeMenuOnSelect: !isMultiple,
    onKeyDown: _onKeyDown,
    value,
    isClearable: !!onClear,
    defaultMenuIsOpen: initiallyFocused,
    filterOption,
    isDisabled,
    instanceId,
    isSearchable,
    formatGroupLabel,
    ref,
  } as DropdownProps;

  const asyncProps = {
    ...selectProps,
    loadOptions,
  };

  const renderAsyncDropdown = () => (allowCreation ? <AsyncCreatableSelect {...asyncProps} /> : <AsyncSelect {...asyncProps} />);
  const renderSyncDropdown = () => (allowCreation ? <CreatableSelect {...selectProps} /> : <Select {...selectProps} />);

  return (
    <div className={className} data-testid={dataTestId}>
      {label && (
        <label className="mb-1 flex stihl-text-body-sm-reg" htmlFor={htmlFor}>
          {label}
        </label>
      )}
      <div onMouseDown={(e) => e.stopPropagation()}>{isAsync ? renderAsyncDropdown() : renderSyncDropdown()}</div>
    </div>
  );
});

const IndicatorsContainer = (showIndicators: boolean) => {
  return showIndicators ? components.IndicatorsContainer : () => null;
};

const IndicatorSeparator = () => null;

const ClearIndicator = (showClearIndicator: boolean) => (showClearIndicator ? components.ClearIndicator : () => null);

const customStyles: StylesConfig<unknown, true, GroupBase<unknown>> = {
  valueContainer: (provided) => ({
    ...provided,
    ':hover': {
      ...provided[':hover'],
      borderColor: `${themeColors.primaryBlack}`,
    },
    ':focus': {
      ...provided[':focus'],
      outlineColor: `${themeColors.primaryBlack}`,
      outlineWidth: '1px',
      outlineStyle: 'solid',
      outlineOffset: 0,
    },
  }),
  multiValue: (provided) => {
    return {
      ...provided,
      backgroundColor: 'transparent',
    };
  },
  multiValueRemove: (provided) => ({
    ...provided,
    ':hover': {
      ...provided[':hover'],
      backgroundColor: 'transparent',
      color: themeColors.secondaryDeep as string,
    },
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: `${themeColors.secondaryDeep}`,
    cursor: 'pointer',
    ':hover': {
      color: `${themeColors.secondaryDeep}`,
    },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: `${themeColors.secondaryDeep}`,
    cursor: 'pointer',
    ':hover': {
      color: `${themeColors.secondaryDeep}`,
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'rgba(0, 0, 0, 0.6)',
  }),
  menu: (provided, state) => ({
    ...provided,
    marginTop: '-1px',
    marginLeft: '-1px',
    zIndex: 11,
    border: `2px solid ${themeColors.secondaryDeep}`,
    outline: 'none',
    display: state.options.length ? 'inline' : 'none',
    width: 'calc(100% + 2px)',
  }),
  option: (provided, { isSelected, isFocused, isDisabled }) => ({
    ...provided,
    backgroundColor: isFocused ? `${themeColors.secondaryLight}` : 'transparent',
    fontSize: 14,
    fontWeight: isSelected ? 'bold' : 'normal',
    color: `${themeColors.secondaryDeep}`,
    minHeight: '2rem',
    ':hover': {
      ...provided[':hover'],
      backgroundColor: `${themeColors.secondaryLight}`,
      cursor: isDisabled ? 'cursor' : 'pointer',
    },
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    backgroundColor: 'transparent',
    color: `${themeColors.secondaryDeep}`,
    minHeight: '2rem',
    textAlign: 'left',
  }),
  loadingMessage: (provided) => ({
    ...provided,
    backgroundColor: 'transparent',
    fontSize: 14,
    color: `${themeColors.secondaryDeep}`,
    minHeight: '2rem',
  }),
  group: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
};

const filterConfig = {
  ignoreCase: true,
  ignoreAccents: true,
  trim: false,
  matchFrom: 'any' as const,
  stringify: (option: SelectOption) => option.label,
};

const formatGroupLabel = (data: GroupedOption) => {
  return <div className="normal-case text-primary-black stihl-text-h5-desktop">{data.label}</div>;
};
